import { api } from "../../lib/api";

export type SaleItem = {
  id: number;
  productId: number;
  productName: string;
  unitPriceCents: number;
  qty: number;
  lineDiscountCents: number;
  lineSubtotalCents: number;
  sizeGuideId?: number | null;
  sizeGuideName: string | null;
  size: string | null;
  note: string | null;
};

export type SettlementRef = {
  id: number;
  settledOn: string;
  grossCents: number;
  transferredCents: number;
  commissionCents: number;
  status: "active" | "reverted";
};

export type SalePayment = {
  id: number;
  paymentMethodId: number;
  paymentMethodSnapshot: {
    name: string;
    color: string;
    icon: string;
    discountPct: number;
    requiresNetReceived: boolean;
    requiresCustomer: boolean;
    settlesInBatch: boolean;
  };
  amountCents: number;
  netReceivedCents: number | null;
  commissionCents: number;
  // ClubCred-style payments (settlesInBatch): null hasta que una liquidación
  // agrupa este pago. Nunca se completa netReceivedCents/commissionCents acá
  // — el neto real solo existe a nivel de la liquidación (settlement.*).
  settlementId: number | null;
  settlement: SettlementRef | null;
};

export type Sale = {
  id: number;
  createdAt: string;
  operatorId: number;
  status: "active" | "voided";
  operationType?: "sale" | "consignment";
  consignmentStatus?: "open" | "converted" | "discarded" | null;
  voidedAt: string | null;
  voidedById: number | null;
  voidReason: string | null;
  items: SaleItem[];
  payments: SalePayment[];
  itemsSubtotalCents: number;
  lineDiscountTotalCents: number;
  globalDiscountCents: number;
  paymentMethodDiscountCents: number;
  totalChargedCents: number;
  netReceivedCents: number | null;
  commissionCents: number;
  paymentMethodId: number;
  paymentMethodSnapshot: {
    name: string;
    color: string;
    icon: string;
    discountPct: number;
    requiresNetReceived: boolean;
    requiresCustomer: boolean;
    settlesInBatch: boolean;
  };
  customerId: number | null;
  customerNameSnapshot: string | null;
  paymentMethodDiscountApplied: boolean;
  createdFromClientId: string | null;
};

export type SplitPaymentInput = {
  paymentMethodId: number;
  amountCents: number;
  netReceivedCents?: number;
};

export type CreateSaleInput = {
  clientId: string;
  paymentMethodId: number;
  globalDiscountCents?: number;
  customerId?: number | null;
  netReceivedOverride?: number;
  skipNetReceived?: boolean;
  /** Token returned by the API after reviewing an unusual monetary amount. */
  moneyConfirmationToken?: string;
  payments?: SplitPaymentInput[];
  items: Array<{
    productId: number;
    productName: string;
    unitPriceCents: number;
    qty: number;
    lineDiscountCents?: number;
    sizeGuideId?: number | null;
    size?: string | null;
    note?: string | null;
  }>;
};

export type DailySummary = {
  date: string | null;
  from: string | null;
  to: string | null;
  salesCount: number;
  grossSalesCents: number;
  lineDiscountCents: number;
  globalDiscountCents: number;
  paymentMethodDiscountCents: number;
  totalChargedCents: number;
  commissionCents: number;
  // Denominador correcto de commissionCents / commissionBaseCents: suma de lo
  // cobrado con medios que descuentan comisión, calculada con el snapshot
  // histórico de cada venta (no con el estado actual del medio de pago).
  commissionBaseCents: number;
  // Comisión y base de cálculo por medio de pago, en centavos. Clave:
  // paymentMethodId como string. Solo incluyen medios con pagos que
  // efectivamente tuvieron comisión calculable (requiresNetReceived y con
  // el neto ya cargado); un medio sin pagos que califiquen no aparece.
  commissionByMethod: Record<string, number>;
  commissionBaseByMethod: Record<string, number>;
  totalChargedByMethod: Record<number, number>;
  netReceivedByMethod: Record<number, number>;
  salesReceivedCents: number;
  consignmentAdvancesCents: number;
  consignmentAdvancesByMethod: Record<string, number>;
  consignmentRefundsCents: number;
  consignmentRefundsByMethod: Record<string, number>;
  creditSalesCents: number;
  pendingNetCents: number;
  // Suma de pagos con settlesInBatch que todavía no fueron incluidos en
  // ninguna liquidación (settlementId === null). No suma al día hasta que
  // se liquide — igual que pendingNetCents pero para el otro flujo.
  pendingSettlementCents: number;
  // Liquidaciones (PaymentSettlement) confirmadas cuyo settledOn cae en el
  // rango consultado, sin importar cuándo se hicieron las ventas que
  // agrupan. Esto sí suma a netDayCents.
  settlementsReceivedCents: number;
  settlementCommissionCents: number;
  settlementsReceivedByMethod: Record<string, number>;
  settlementCommissionByMethod: Record<string, number>;
  manualIncomeCents: number;
  manualExpenseCents: number;
  cobrosCtaCteCents: number;
  netDayCents: number;
  unitsSold: number;
  unitsByProduct: Array<{ productId: number; productName: string; units: number; salesCents: number }>;
  unitsByCategory: Array<{ categoryId: number | null; categoryName: string; units: number; salesCents: number }>;
  // Breakdown by payment method. Key is the paymentMethodId as string, or
  // "none" when the account movement has no payment method attached.
  cobrosCtaCteByMethod: Record<string, number>;
  manualIncomeByConcept: Array<{ conceptId: number | null; conceptName: string; amountCents: number }>;
  manualExpenseByConcept: Array<{ conceptId: number | null; conceptName: string; amountCents: number }>;
  unitsByProductSize: Array<{ productId: number; productName: string; size: string | null; units: number; salesCents: number }>;
  // Nombre y flags de cada medio de pago tomados del snapshot histórico de
  // las ventas del rango consultado. Clave: paymentMethodId como string.
  // No incluye ids que solo aparecen en movimientos de cuenta corriente
  // (AccountMovement no guarda snapshot).
  paymentMethodMeta: Record<string, { name: string; requiresNetReceived: boolean }>;
};

export const salesApi = {
  create: (body: CreateSaleInput) =>
    api<Sale>("/api/sales", { body }),
  list: (params: { from?: string; to?: string; operatorId?: number; status?: "active" | "voided"; pendingNet?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    if (params.operatorId) q.set("operatorId", String(params.operatorId));
    if (params.status) q.set("status", params.status);
    if (params.pendingNet) q.set("pendingNet", "true");
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return api<Sale[]>(`/api/sales${qs ? `?${qs}` : ""}`);
  },
  get: (id: number) => api<Sale>(`/api/sales/${id}`),
  // `paymentId` is required for sales with split payments, to indicate which
  // payment's net is being set/corrected. Optional for single-payment sales.
  setNetReceived: (
    id: number,
    netReceivedCents: number,
    paymentId?: number,
    moneyConfirmationToken?: string
  ) =>
    api<Sale>(`/api/sales/${id}/net-received`, {
      method: "PATCH",
      body: {
        netReceivedCents,
        ...(paymentId != null ? { paymentId } : {}),
        ...(moneyConfirmationToken ? { moneyConfirmationToken } : {}),
      },
    }),
  voidSale: (id: number, reason: string) =>
    api<Sale>(`/api/sales/${id}/void`, { method: "POST", body: { reason } }),
};

export const summaryApi = {
  daily: (params: { from?: string; to?: string; range?: "all" } = {}) => {
    const query = new URLSearchParams();
    if (params.range === "all") {
      // Histórico: no date filter at all, ignore from/to.
      query.set("range", "all");
    } else {
      if (params.from) query.set("from", params.from);
      if (params.to) query.set("to", params.to);
    }
    const q = query.toString();
    return api<DailySummary>(`/api/summary/daily${q ? `?${q}` : ""}`);
  },
};
